import { AbstractMesh, GPUParticleSystem, Mesh, MeshBuilder, ParticleSystem, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Fire Trap Behavior - Periodic fire column that activates/deactivates
 * When active: mesh scales up on configured axis and fire particles emit
 * When inactive: mesh scales down, no particles
 * Supports configurable axis (x/y/z) and direction (+1/-1) for horizontal/vertical fire
 * Creates invisible collision mesh during active phase that kills player
 */

export class ObstacleFireBehavior extends ObstacleBehavior {
  private time: number = 0;
  private isActive: boolean = false;
  private fireParticles: ParticleSystem | GPUParticleSystem = null; // ParticleSystem
  private collisionMesh: Mesh = null;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleFire', startPosition, pathIndex, config);
  }

  attach(mesh: AbstractMesh): void {
    super.attach(mesh);
    if (this.config.fireEmitterGrow) {
      // Start with mesh scaled down (inactive) on the configured axis
      this.setScale(0.1);
    }
  }

  private setScale(value: number): void {
    switch (this.config.axis) {
      case 'x':
        this.mesh.scaling.x = value * this.config.direction;
        break;
      case 'y':
        this.mesh.scaling.y = value;
        break;
      case 'z':
        this.mesh.scaling.z = value * this.config.direction;
        break;
    }
  }

  private getScale(): number {
    switch (this.config.axis) {
      case 'x':
        return Math.abs(this.mesh.scaling.x);
      case 'y':
        return this.mesh.scaling.y;
      case 'z':
        return Math.abs(this.mesh.scaling.z);
    }
  }

  initLevel(level: Level): ObstacleFireBehavior {
    super.initLevel(level);
    // Create fire particle system with direction and width
    this.fireParticles = this.level.particles.createFireColumnParticleSystem(
      this.mesh,
      this.config.fireHeight,
      this.config.axis,
      this.config.direction,
      this.config.fireWidth
    );

    // Create invisible collision mesh for killing player - dimensions based on fireWidth
    this.collisionMesh = MeshBuilder.CreateBox(
      'fireCollision',
      { width: this.config.fireWidth, height: this.config.fireHeight, depth: this.config.fireWidth },
      this.level.scene
    );
    this.collisionMesh.isVisible = false;
    this.collisionMesh.isPickable = false;
    this.collisionMesh.parent = this.mesh;

    // Position collision mesh based on axis and direction
    switch (this.config.axis) {
      case 'x':
        this.collisionMesh.rotation.z = Math.PI / 2;
        this.collisionMesh.position.x = (this.config.fireHeight / 2) * this.config.direction;
        break;
      case 'y':
        this.collisionMesh.position.y = this.config.fireHeight / 2;
        break;
      case 'z':
        this.collisionMesh.rotation.x = Math.PI / 2;
        this.collisionMesh.position.z = (this.config.fireHeight / 2) * this.config.direction;
        break;
    }

    // Set metadata for player collision detection
    this.collisionMesh.metadata = {
      playerCollision: new PlayerCollision(
        false, // disableCollision
        (level, player, event) => {
          if (player.vulnerable && !level.game.config.godMode) {
            player.collide(event, 'Killed by fire!');
          }
        }
      ),
    };

    // Initially disabled
    this.collisionMesh.setEnabled(false);

    this.level.player?.registerCollisionMesh(this.collisionMesh);

    return this;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    const cycleDuration = this.config.fireInterval + this.config.fireActiveTime;
    const cycleTime = this.time % cycleDuration;

    const shouldBeActive = cycleTime >= this.config.fireInterval;

    if (shouldBeActive !== this.isActive) {
      this.isActive = shouldBeActive;
      if (this.isActive) {
        // Activate fire
        this.fireParticles?.start();
        this.level?.game.sound.play('fire');
        // Enable collision mesh and register with player
        this.collisionMesh?.setEnabled(true);
        if (this.collisionMesh) {
          this.level?.player?.registerCollisionMesh(this.collisionMesh);
        }
      } else {
        // Deactivate fire
        this.fireParticles?.stop();
        // Disable collision mesh and unregister
        if (this.collisionMesh) {
          this.level?.player?.unregisterCollisionMesh(this.collisionMesh);
        }
        this.collisionMesh?.setEnabled(false);
      }
    }

    if (this.config.fireEmitterGrow) {
      // Animate mesh scale based on active state
      const targetScale = this.isActive ? this.config.fireHeight : 0.1;
      const currentScale = this.getScale();
      const lerpSpeed = 8;
      const newScale = currentScale + (targetScale - currentScale) * Math.min(1, deltaTime * lerpSpeed);
      this.setScale(newScale);
    }
  }

  clone(): ObstacleFireBehavior {
    return new ObstacleFireBehavior(this.startPosition, this.pathIndex, this.config);
  }

  detach(): void {
    super.detach();
    this.fireParticles?.dispose();
    if (this.collisionMesh) {
      this.level?.player?.unregisterCollisionMesh(this.collisionMesh);
      this.collisionMesh.dispose();
    }
  }
}
